/**
 * 充值订单的退款弹窗：先查退款状态（可退余额、已有退款与追回进度），再填金额与原因发起退款。
 *
 * 背景：Alice 的退款是微信原路退回；本仓只有 Stripe，退款走支付服务 POST /billing/admin/orders/{id}/refund，
 *   Stripe 受理后按比例追回用户的花瓣，追回由 Webhook 或对账任务推进，所以弹窗里要能看到「待追回」的笔数。
 * 设计意图：
 *   - 打开时请求 /billing/admin/recharge-orders/{id}/refund-status，金额默认填可退余额；这是只读查询，不写任何东西。
 *   - 金额以主币种填写（美元、韩元等），换成最小货币单位再提交；韩元、日元没有小数位。
 *   - 失败留在弹窗里显示错误，成功后关闭并让页面刷新列表。
 * 关键约束：退款要 admin 且不可撤销，原因 2 到 500 字写进审计；用户余额可能因追回变负。
 */
import { useEffect, useState } from 'react'
import { usersApi } from '@ap/api-client/admin-users'
import { fmtMinor } from '@ap/api-client/money'
import { fmtDateTime, useToast } from '@ap/ui'
import { PILL, Pill } from '../kit-runtime2.tsx'
import { UModal, fieldStyle, useQuery, useU } from '../kit-users.tsx'

const digitsOf = (currency: string) => (['KRW', 'JPY'].includes(currency.toUpperCase()) ? 0 : 2)

export function RefundModal({ orderId, onClose, onDone }: { orderId: string | null; onClose: () => void; onDone: () => void }) {
  const { t, G, err, tag, locale } = useU()
  const O = G.orders
  const toast = useToast()
  const info = useQuery(() => usersApi.refundStatus(orderId ?? ''), [orderId], orderId !== null)
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (orderId) {
      setAmount('')
      setReason('')
      setError('')
    }
  }, [orderId])
  // 取到可退余额后把它填进金额框（用户没改过才填）
  useEffect(() => {
    const d = info.data
    if (d && orderId && amount === '') setAmount((d.refundable_minor / 10 ** digitsOf(d.currency)).toFixed(digitsOf(d.currency)))
  }, [info.data]) // eslint-disable-line react-hooks/exhaustive-deps

  const d = info.data
  const digits = d ? digitsOf(d.currency) : 2
  const minor = Math.round(Number(amount) * 10 ** digits)
  const valid = !!d && Number.isFinite(minor) && minor > 0 && minor <= d.refundable_minor && reason.trim().length >= 2

  const submit = async () => {
    if (!orderId || !valid) return
    setBusy(true)
    setError('')
    try {
      await usersApi.refundOrder(orderId, minor, reason.trim())
      toast.ok(t(O.refundDone))
      onDone()
      onClose()
    } catch (e) {
      console.error('[g2] 退款失败', e)
      setError(err(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <UModal
      open={orderId !== null}
      title={t(O.refundTitle)}
      onClose={onClose}
      busy={busy}
      width={560}
      footer={
        <>
          <button className="btn btn-secondary" disabled={busy} onClick={onClose}>
            {t(G.common.cancel)}
          </button>
          <button className="btn btn-danger" disabled={busy || !valid} onClick={submit}>
            {t(O.refundBtn)}
          </button>
        </>
      }
    >
      {info.error != null && <div className="ops-note" style={{ color: 'var(--error-text)', marginTop: 0 }}>{t(G.common.loadFailed, { msg: err(info.error) })}</div>}
      {info.loading && !d && <div className="u2-note">{t(G.common.loading)}</div>}
      {d && (
        <>
          <div className="ops-note" style={{ marginTop: 0 }}>
            {t(O.refundInfo, {
              id: d.order_id.slice(0, 8),
              amount: fmtMinor(d.amount_minor, d.currency, tag),
              refunded: fmtMinor(d.refunded_minor, d.currency, tag),
            })}
            <div style={{ marginTop: 4 }}>{t(O.refundWarn)}</div>
          </div>
          {error && <div className="ops-note" style={{ color: 'var(--error-text)' }}>{error}</div>}
          <div className="form-group">
            <label>
              {t(O.refundAmount)} ({d.currency.toUpperCase()})
            </label>
            <input
              type="number"
              min={1 / 10 ** digits}
              max={d.refundable_minor / 10 ** digits}
              step={1 / 10 ** digits}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              style={{ ...fieldStyle, width: '100%' }}
            />
            <div className="u2-note" style={{ marginTop: 4 }}>
              {t(O.refundMax, { v: fmtMinor(d.refundable_minor, d.currency, tag) })}
            </div>
          </div>
          <div className="form-group">
            <label>{t(G.common.reason)}</label>
            <textarea maxLength={500} rows={2} value={reason} onChange={(e) => setReason(e.target.value)} style={{ ...fieldStyle, width: '100%' }} />
            <div className="u2-note" style={{ marginTop: 4 }}>
              {t(G.common.reasonHint)}
            </div>
          </div>
          <div className="d-section-title" style={{ marginTop: 14 }}>
            {t(O.refundProgress)}
          </div>
          {d.pending_clawback > 0 && <div className="ops-note" style={{ marginTop: 0 }}>{t(O.refundPendingClaw, { n: d.pending_clawback })}</div>}
          {d.refunds.length === 0 ? (
            <div className="u2-note">{t(O.refundNoRecords)}</div>
          ) : (
            <div className="user-detail-table-wrap" style={{ fontSize: 12 }}>
              <table>
                <thead>
                  <tr>
                    <th>{t(G.common.time)}</th>
                    <th>{t(O.refundColAmount)}</th>
                    <th>{t(O.refundColStatus)}</th>
                    <th>{t(O.refundColClaw)}</th>
                  </tr>
                </thead>
                <tbody>
                  {d.refunds.map((r) => (
                    <tr key={r.id}>
                      <td style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(r.created_at, locale)}</td>
                      <td>{fmtMinor(r.amount_minor, d.currency, tag)}</td>
                      <td>
                        <Pill tone={r.status === 'succeeded' ? PILL.green : PILL.gray}>{r.status}</Pill>
                      </td>
                      <td>
                        <Pill tone={r.clawed_at ? PILL.green : PILL.amber}>{t(r.clawed_at ? O.clawed : O.clawPending)}</Pill>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </UModal>
  )
}
